import React from 'react';
import { TableProps } from '@amzn/awsui-components-console/table';
import Link from '@amzn/awsui-components-console/link';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';

import {
  ActiveTranslationJob,
  HistoricalTranslationJob,
  TranslationStatus,
} from '../types';
import { formatDate, computeTurnaround } from 'src/common/utils/dateFormatters';

/** Maps translation status to the appropriate Cloudscape status indicator. */
const getStatusIndicator = (status: TranslationStatus) => {
  switch (status) {
    case TranslationStatus.InProgress:
      return <StatusIndicator type="in-progress">IN_PROGRESS</StatusIndicator>;
    case TranslationStatus.Complete:
      return <StatusIndicator type="success">Complete</StatusIndicator>;
    default:
      return <StatusIndicator type="info">{status}</StatusIndicator>;
  }
};

/**
 * Build href for a string group detail page.
 *
 * TODO: return `/${namespaceId}/${stringGroupId}` once the string group detail
 * route exists in App.tsx. Today there is no 2-segment route, so a real path
 * would fall through to the `*` catch-all and redirect to the landing page —
 * '#' is the same placeholder href SearchResultsTable uses.
 */
const buildStringGroupHref = (_item: {
  namespaceId: string;
  stringGroupId: string;
}): string => '#';

/**
 * String group cell link, inert until the detail route exists.
 *
 * The href is a '#' placeholder, so the default action is prevented: following
 * it would otherwise append '#' to the URL and jump the page to the top, which
 * looks like a broken link rather than an unimplemented one. The href is still
 * rendered so the cell is a real link for middle-click and screen readers, and
 * so wiring the route later is a change in one place.
 *
 * TODO: when the route lands, take `navigate` and follow the namespace column's
 * onFollow + navigate pattern instead of preventing the default outright.
 */
const StringGroupLink: React.FC<{
  item: { namespaceId: string; stringGroupId: string };
}> = ({ item }) => (
  <Link href={buildStringGroupHref(item)} onFollow={(e) => e.preventDefault()}>
    {item.stringGroupId}
  </Link>
);

/**
 * Creates active job column definitions with SPA-aware links.
 * Uses onFollow + preventDefault to avoid full page reloads and
 * Midway/Cognito re-auth round-trips.
 */
export const createActiveColumnDefinitions = (
  navigate: (path: string) => void,
): TableProps.ColumnDefinition<ActiveTranslationJob>[] => [
  {
    id: 'stringGroup',
    header: 'String Group',
    cell: (item) => <StringGroupLink item={item} />,
  },
  {
    id: 'namespace',
    header: 'Namespace',
    cell: (item) => (
      <Link
        href={`/${item.namespaceId}`}
        onFollow={(e) => {
          e.preventDefault();
          navigate(`/${item.namespaceId}`);
        }}
      >
        {item.namespaceId}
      </Link>
    ),
  },
  {
    id: 'version',
    header: 'Version',
    cell: (item) => `V${item.stringGroupVersion}`,
  },
  {
    id: 'trackingId',
    header: 'Tracker ID',
    cell: (item) => item.trackingId,
  },
  {
    id: 'clientId',
    header: 'Source',
    cell: (item) => item.clientId,
  },
  {
    id: 'sentForTranslation',
    header: 'Sent for Translation',
    cell: (item) => formatDate(item.sentForTranslationDate),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => getStatusIndicator(item.translationStatus),
  },
  {
    id: 'dueDate',
    header: 'Due Date',
    cell: (item) => formatDate(item.dueDate),
  },
];

/**
 * Creates historical job column definitions with SPA-aware links.
 */
export const createHistoricalColumnDefinitions = (
  navigate: (path: string) => void,
): TableProps.ColumnDefinition<HistoricalTranslationJob>[] => [
  {
    id: 'stringGroup',
    header: 'String Group',
    cell: (item) => <StringGroupLink item={item} />,
  },
  {
    id: 'namespace',
    header: 'Namespace',
    cell: (item) => (
      <Link
        href={`/${item.namespaceId}`}
        onFollow={(e) => {
          e.preventDefault();
          navigate(`/${item.namespaceId}`);
        }}
      >
        {item.namespaceId}
      </Link>
    ),
  },
  {
    id: 'version',
    header: 'Version',
    cell: (item) => `V${item.stringGroupVersion}`,
  },
  {
    id: 'clientId',
    header: 'Source',
    cell: (item) => item.clientId,
  },
  {
    id: 'trackingId',
    header: 'Tracker ID',
    cell: (item) => item.trackingId,
  },
  {
    id: 'sentOn',
    header: 'Sent On',
    cell: (item) => formatDate(item.sentForTranslationDate),
  },
  {
    id: 'deliveredOn',
    header: 'Delivered On',
    cell: (item) => formatDate(item.deliveredOnDate),
  },
  {
    id: 'turnaround',
    header: 'TAT',
    cell: (item) =>
      computeTurnaround(item.sentForTranslationDate, item.deliveredOnDate),
  },
  {
    id: 'failure',
    header: 'Job Failure',
    cell: (item) => {
      const failed = item.translationStatus === TranslationStatus.Failed;
      return (
        <StatusIndicator type={failed ? 'error' : 'success'}>
          {failed ? 'Yes' : 'No'}
        </StatusIndicator>
      );
    },
  },
  {
    id: 'cancelled',
    header: 'Cancelled Job',
    cell: (item) => {
      const cancelled = item.translationStatus === TranslationStatus.Cancelled;
      return (
        <StatusIndicator type={cancelled ? 'warning' : 'success'}>
          {cancelled ? 'Yes' : 'No'}
        </StatusIndicator>
      );
    },
  },
];
